import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import {oauthApi} from '../../../../repositories/oauth';
import contactApi from '../../../../repositories/contact';
import { EmailContact, OAuthStatus } from '../../../../types/email';
import { handleError } from '../../../../utils/errorHandler';
import OAuthConnectionStatus from './OAuthConnectionStatus';
import ContactList from './ContactList';
import AddContactModal from './AddContactModal';
import BulkUploadModal from './BulkUploadModal';
import SettingsPanel from '../../../ui/SettingsPanel';
import Button from '../../../ui/Button';
import { swalColors } from '../../../../utils/swal';

/** Human labels for the search field keys, used in the input placeholder. */
const SEARCH_FIELD_LABELS: Record<string, string> = {
  firstName: 'first name',
  lastName: 'last name',
  email: 'email',
  company: 'company',
};

const Contacts: React.FC = () => {
  const [oauthStatus, setOAuthStatus] = useState<OAuthStatus>({
    gmail: { connected: false, email: null, connectedAt: null, lastRefreshedAt: null },
    outlook: { connected: false, email: null, connectedAt: null, lastRefreshedAt: null },
  });
  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [searchType, setSearchType] = useState<'firstName' | 'lastName' | 'email' | 'company'>('firstName');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState<boolean>(false);
  const [editingContact, setEditingContact] = useState<EmailContact | null>(null);
  const [connecting, setConnecting] = useState<'gmail' | 'outlook' | null>(null);
  /*
   * The default status has both providers disconnected, which is also what a
   * connected user looks like until the request lands — so without this the
   * "Connect Your Email Account" prompt flashes up before being replaced by the
   * connected card. Start true so the first paint is a placeholder, not a claim.
   */
  const [oauthLoading, setOAuthLoading] = useState<boolean>(true);

  // Fetch OAuth status
  const fetchOAuthStatus = async () => {
    try {
      const response = await oauthApi.getEmailStatus();
      if (response?.data) {
        setOAuthStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch OAuth status:', error);
    } finally {
      setOAuthLoading(false);
    }
  };

  // Fetch contacts
  const fetchContacts = async (page = pagination.page, isSearch = false) => {
    try {
      if (isSearch) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      const response = await contactApi.getContacts({
        page,
        limit: pagination.limit,
        searchType: debouncedSearch.trim() ? searchType : undefined,
        search: debouncedSearch.trim() || undefined,
      });
      if (response?.data?.success) {
        setContacts(response.data.contacts);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchOAuthStatus();
    fetchContacts(1);
  }, []);

  // Reset connecting state and re-check OAuth status when user returns
  // from Google OAuth page (e.g. hits back button without completing auth)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setConnecting(null);
        fetchOAuthStatus();
      }
    };

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setConnecting(null);
        fetchOAuthStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isInitialMount = useRef(true);
  // Term the last search request actually used, so we can tell "the user
  // cleared a search" (refetch, to restore the full list) from "the user only
  // switched the field with an empty box" (nothing to do).
  const lastSearchedTerm = useRef('');
  useEffect(() => {
    const term = debouncedSearch.trim();

    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSearchedTerm.current = term;
      return;
    }

    // Switching the field with an empty box has nothing to search: fetchContacts
    // omits searchType entirely without a term, so the request would return the
    // same unfiltered list already on screen.
    if (!term && !lastSearchedTerm.current) return;

    lastSearchedTerm.current = term;
    fetchContacts(1, true);
  }, [debouncedSearch, searchType]);

  const handlePageChange = (newPage: number) => {
    fetchContacts(newPage);
  };

  const handleConnectEmail = async () => {
    try {
      setConnecting('gmail');
      const response = await oauthApi.ConnectGmail();
      console.log('OAuth Connect Response:', response.data.url);
      if (response?.data?.success && response.data.url) {
        console.log('Redirecting to OAuth URL:', response.data.url);
        window.location.href = response.data.url;
      }
    } catch (error) {
      handleError(error);
      setConnecting(null);
    }
  };

  const handleConnectOutlook = async () => {
    try {
      setConnecting('outlook');
      const response = await oauthApi.ConnectOutlook();
      if (response?.data?.success && response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      handleError(error);
      setConnecting(null);
    }
  };

  const handleDisconnectEmail = async () => {
    const result = await Swal.fire({
      title: 'Disconnect Email?',
      text: 'You will need to reconnect to send emails.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalColors.danger,
      confirmButtonText: 'Yes, disconnect',
    });

    if (result.isConfirmed) {
      try {
        const isOutlook = oauthStatus.outlook?.connected;
        const response = await (isOutlook
          ? oauthApi.DisconnectOutlook()
          : oauthApi.DisconnectGmail());
        if (response?.data?.success) {
          setOAuthStatus((prev) => ({
            ...prev,
            [isOutlook ? 'outlook' : 'gmail']: { connected: false, email: null, connectedAt: null },
          }));
          Swal.fire({
            title: 'Disconnected!',
            text: 'Your email has been disconnected.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setIsAddModalOpen(true);
  };

  const handleEditContact = (contact: EmailContact) => {
    setEditingContact(contact);
    setIsAddModalOpen(true);
  };

  const handleDeleteContact = async (id: number) => {
    const contact = contacts.find((c) => c.id === id);
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete ${contact?.firstName} ${contact?.lastName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalColors.danger,
      confirmButtonText: 'Yes, delete',
    });

    if (result.isConfirmed) {
      try {
        const response = await contactApi.deleteContact(id);
        if (response?.data?.success) {
          await fetchContacts();
          Swal.fire({
            title: 'Deleted!',
            text: 'Contact has been deleted.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleSaveContact = async (data: Omit<EmailContact, 'id' |'isGlobal' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editingContact) {
        const response = await contactApi.updateContact(editingContact.id, data);
        if (response?.data?.success) {
          await fetchContacts();
          setIsAddModalOpen(false);
          Swal.fire({
            title: 'Success!',
            text: 'Contact updated successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        }
      } else {
        const response = await contactApi.createContact(data);
        if (response?.data?.success) {
          await fetchContacts();
          setIsAddModalOpen(false);
          Swal.fire({
            title: 'Success!',
            text: 'Contact added successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        }
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleBulkUploadComplete = () => {
    fetchContacts();
    setIsBulkUploadModalOpen(false);
  };

  return (
    /*
      Two panels rather than one: the mail-account connection and the contact
      list are separate concerns, and they were previously divided only by an
      <hr /> inside a single card.
    */
    <>
      <SettingsPanel
        className="mb-4 sm:mb-6"
        title="Connected Account"
        description="The mailbox your pitches are sent from"
      >
        <OAuthConnectionStatus
          ConnectionStatus={oauthStatus}
          onConnectGmail={handleConnectEmail}
          onConnectOutlook={handleConnectOutlook}
          onDisconnect={handleDisconnectEmail}
          connecting={connecting}
          loading={oauthLoading}
        />
      </SettingsPanel>

      <SettingsPanel
        title="Contact Management"
        description="Manage the editors and curators you pitch to"
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setIsBulkUploadModalOpen(true)}
            >
              Bulk Upload CSV
            </Button>
            <Button
              onClick={handleAddContact}
            >
              Add Contact
            </Button>
          </>
        }
      >

      {/*
        Search type + term as one control, matching every other search on the
        app: single border, focus-within treatment, inset divider, custom
        chevron (the native select arrow was the odd one out here).
      */}
      <div className="flex items-stretch w-full mb-4 bg-white border rounded-lg border-neutral-300 focus-within:border-primary-500">
        <div className="relative shrink-0">
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as 'firstName' | 'lastName' | 'email' | 'company')}
            aria-label="Search contacts by field"
            className="w-[104px] sm:w-auto h-full py-2 pl-3 text-sm truncate bg-transparent border-0 appearance-none cursor-pointer pr-7 text-neutral-600 focus:outline-none focus:ring-0"
          >
            <option value="firstName">First Name</option>
            <option value="lastName">Last Name</option>
            <option value="email">Email</option>
            <option value="company">Company</option>
          </select>
          <svg className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-2 top-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <span aria-hidden="true" className="self-stretch w-px my-1.5 bg-neutral-200" />

        <div className="relative flex-1 min-w-0">
          <svg className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            // The old placeholder printed the raw field key ("Search by
            // firstName..."), so the labels are mapped explicitly.
            placeholder={`Search by ${SEARCH_FIELD_LABELS[searchType] ?? searchType}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full py-2 text-sm text-black bg-transparent border-0 rounded-r-lg pl-9 pr-9 placeholder-neutral-400 focus:outline-none focus:ring-0"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="border border-transparent absolute p-0.5 -translate-y-1/2 rounded right-2 top-1/2 text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Contact List */}
      <ContactList
        contacts={contacts}
        onEdit={handleEditContact}
        onDelete={handleDeleteContact}
        loading={loading}
        searchLoading={searchLoading}
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      {/* Add/Edit Contact Modal */}
      <AddContactModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveContact}
        contact={editingContact}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onComplete={handleBulkUploadComplete}
      />
      </SettingsPanel>
    </>
  );
};

export default Contacts;
